/**
 * Email Service
 * AIDLC Administrative Frontend
 *
 * API calls for email management (signature templates and signature profiles). The inbox and OneDrive calls
 * were removed with their routes (KD-1b).
 */

import apiClient from './api';
import type {
  EmailSignatureTemplate,
  EmailIntegrationConfig,
} from '../types/email';

const BASE = '/email';

// ─── Signature ────────────────────────────────────────────────────

export async function getSignatureTemplate(mailbox: string): Promise<EmailSignatureTemplate> {
  const response = await apiClient.get<EmailSignatureTemplate>(`${BASE}/signature`, {
    params: { mailbox },
  });
  return response.data;
}

export async function updateSignatureTemplate(
  mailbox: string,
  template: Partial<EmailSignatureTemplate>
): Promise<EmailSignatureTemplate> {
  const response = await apiClient.put<EmailSignatureTemplate>(`${BASE}/signature`, template, {
    params: { mailbox },
  });
  return response.data;
}

export async function getSignaturePreview(mailbox: string): Promise<string> {
  const response = await apiClient.get<{ html: string }>(`${BASE}/signature/preview`, {
    params: { mailbox },
  });
  return response.data.html;
}

// ─── Integrations ─────────────────────────────────────────────────

export async function getIntegrations(): Promise<EmailIntegrationConfig[]> {
  const response = await apiClient.get<EmailIntegrationConfig[]>(`${BASE}/integrations`);
  return response.data;
}

export async function addIntegration(
  data: { userEmail: string; displayName: string }
): Promise<EmailIntegrationConfig> {
  const response = await apiClient.post<EmailIntegrationConfig>(`${BASE}/integrations`, data);
  return response.data;
}

export async function removeIntegration(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/integrations/${id}`);
}

export async function updateIntegration(
  id: string,
  updates: Partial<Pick<EmailIntegrationConfig, 'displayName' | 'protected' | 'features'>>
): Promise<EmailIntegrationConfig> {
  const response = await apiClient.patch<EmailIntegrationConfig>(`${BASE}/integrations/${id}`, updates);
  return response.data;
}
